/**
 * What the Voice instructions screen needs to know about the instruction templates: the two models, the {{placeholders}} each
 * accepts, the size limits and the same check the database makes on a save. A copy of packages/shared/prompts/prompt_templates.ts
 * (the web app does not import the shared package); lib/test/promptTemplates.test.ts keeps the two identical.
 */

export const PROMPT_KINDS = [
  { value: "voice", label: "Voice model", blurb: "Speaks to the caller: personality, greeting, how it hands work to the backend." },
  { value: "backend", label: "Backend model", blurb: "Decides what to do and uses the booking tools: context, booking workflow, fixed rules." },
] as const;
export type PromptKind = (typeof PROMPT_KINDS)[number]["value"];

export interface PromptPlaceholder {
  key: string;
  description: string;
  required: boolean;
  /** Used by the preview. */
  example: string;
}

export const PROMPT_TEMPLATE_LIMITS = { minChars: 200, maxChars: 30000 } as const;

export const PROMPT_PLACEHOLDERS: Record<PromptKind, ReadonlyArray<PromptPlaceholder>> = {
  voice: [
    { key: "agent_name", description: "The receptionist's name for this business (Admin > Agent config).", required: true, example: "Alex" },
    { key: "business_name", description: "The business's name.", required: true, example: "Demo Salon" },
    { key: "caller_id_line", description: "A sentence saying whether the caller's number is known, and what to do if asked. Needed so the model never claims it cannot see a number it can.", required: true, example: "Caller ID for this call: 0411 803 973. You genuinely have this -- if asked \"can you see my number?\" or similar, say yes and read it back to confirm, don't say no." },
    { key: "voice_gender", description: "\"a man\", \"a woman\" or \"a person\", from the chosen voice.", required: false, example: "a man" },
    { key: "tone_manner", description: "How the business's chosen tone sounds (for example \"a warm, relaxed manner with an\").", required: false, example: "a warm, relaxed manner with an" },
    { key: "pace_phrase", description: "The business's chosen speaking pace, in words.", required: false, example: "an ordinary conversational pace" },
    { key: "style_line", description: "The business's chosen style, tone and pace notes as one sentence (empty for the default).", required: false, example: "Keep it professional." },
    { key: "greeting_opener", description: "The opening word or two of the greeting for the chosen tone (for example \"Hi there\").", required: false, example: "Hi there" },
  ],
  backend: [
    { key: "business_name", description: "The business's name.", required: true, example: "Demo Salon" },
    { key: "agent_name", description: "The voice agent's name (Admin > Agent config); whose words the reply becomes.", required: false, example: "Alex" },
    { key: "temporary_notices", description: "The business's temporary notices that apply today, with their heading; empty when there are none.", required: false, example: "\nTEMPORARY NOTICES -- ...\n- Closed Monday\n" },
    { key: "now_local", description: "The business's current date and time when the call started. The only clock the model has.", required: true, example: "Saturday 3 October 2026, 4:30 pm" },
    { key: "timezone", description: "The business's timezone.", required: true, example: "Australia/Sydney" },
    { key: "caller_number", description: "The caller's number from Caller ID, or \"unknown\".", required: false, example: "+61411803973" },
    { key: "services", description: "The business's services, one per line, with the reference the model passes to the tools. Without it the model cannot book.", required: true, example: "- Cut: 30 min, $45.00 (service_ref svc1)" },
    { key: "staff", description: "The business's staff, one per line, with their resource ids.", required: true, example: "  - Chris (stylist): resource_id r1" },
    { key: "transfer_routes", description: "The transfer destinations, or \"none\".", required: false, example: "Manager" },
    { key: "business_address_line", description: "A line with the business address; empty when not set.", required: false, example: "\n- Business address: 1 Main St." },
    { key: "opening_hours_block", description: "Lines with the opening hours; empty when not set.", required: false, example: "\n- Business opening hours (...):\n  - Monday 9:00 am to 5:00 pm" },
    { key: "business_knowledge", description: "The business's own notes about itself, with their heading; empty when none.", required: false, example: "\nBUSINESS KNOWLEDGE -- ...\n- Free parking\n" },
    { key: "rules_block", description: "The BUSINESS RULES block (shared rules, then the business's own) with the fixed safeguards; empty when there are none. Keep it so the rules reach the model.", required: true, example: "\n\nBUSINESS RULES -- ...\n- Always say the service name." },
  ],
};

export type PromptTemplateCheck =
  | { ok: true }
  | { ok: false; reason: "too_short" | "too_long" | "unknown_placeholder" | "missing_placeholder"; detail?: string };

const PLACEHOLDER_RE = /\{\{\s*([a-z0-9_]+)\s*\}\}/g;

export function validatePromptTemplate(kind: PromptKind, text: string): PromptTemplateCheck {
  const t = text.trim();
  if (t.length < PROMPT_TEMPLATE_LIMITS.minChars) return { ok: false, reason: "too_short" };
  if (t.length > PROMPT_TEMPLATE_LIMITS.maxChars) return { ok: false, reason: "too_long" };
  const placeholders = PROMPT_PLACEHOLDERS[kind];
  const known = new Set(placeholders.map((p) => p.key));
  const used = new Set<string>();
  for (const m of t.matchAll(PLACEHOLDER_RE)) {
    const key = m[1] ?? "";
    if (!known.has(key)) return { ok: false, reason: "unknown_placeholder", detail: key };
    used.add(key);
  }
  const stray = t.replace(PLACEHOLDER_RE, "").match(/\{\{[^}]*\}\}/);
  if (stray) return { ok: false, reason: "unknown_placeholder", detail: stray[0] };
  for (const p of placeholders) {
    if (p.required && !used.has(p.key)) return { ok: false, reason: "missing_placeholder", detail: p.key };
  }
  return { ok: true };
}

/** Fills the {{placeholders}} in one pass (a value is never read as a placeholder itself). */
export function renderPromptTemplate(template: string, values: Record<string, string>): string {
  return template.replace(PLACEHOLDER_RE, (_all, key: string) => values[key] ?? "");
}

/** A friendly sentence for a failed check. */
export function describePromptCheck(check: Exclude<PromptTemplateCheck, { ok: true }>): string {
  switch (check.reason) {
    case "too_short":
      return `This is too short to be the instructions (at least ${PROMPT_TEMPLATE_LIMITS.minChars} characters).`;
    case "too_long":
      return `This is too long (at most ${PROMPT_TEMPLATE_LIMITS.maxChars.toLocaleString("en-AU")} characters).`;
    case "unknown_placeholder":
      return `\u201C${check.detail ?? ""}\u201D is not a placeholder this model knows. Use the list on the right, written like {{agent_name}}.`;
    case "missing_placeholder":
      return `The text must contain {{${check.detail ?? ""}}}: without it the call would lose something it cannot work without.`;
  }
}
